import { useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useMarket } from "../lib/store";
import { usePortfolio } from "../lib/usePortfolio";
import { useSubscribe, useTick } from "../lib/socket";
import { fmt, istTime } from "../lib/format";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { Panel, PageHeader, th, td, btnGhost, Empty } from "../components/Panel";
import { ShareRowButton } from "../components/community/TgBits";
import { openShareFor, signedPct } from "../lib/community";

const OPEN = ["OPEN", "TRIGGER_PENDING", "PARTIALLY_FILLED"];
const STATUS_CLS = {
  COMPLETE: "text-up bg-up/10", REJECTED: "text-down bg-down/10", CANCELLED: "text-muted-foreground bg-muted",
  OPEN: "text-primary bg-primary/10", TRIGGER_PENDING: "text-amber-500 bg-amber-500/10", PARTIALLY_FILLED: "text-primary bg-primary/10",
};

const Status = ({ o }) => (
  <span className={`text-[10px] font-semibold tracking-wider rounded px-1.5 py-0.5 ${STATUS_CLS[o.status] || ""}`}>
    {o.amo_pending && ["OPEN", "TRIGGER_PENDING"].includes(o.status) ? "AMO REQ" : o.status.replace("_", " ")}
  </span>
);

function Ltp({ k }) {
  const t = useTick(k);
  return <span className="num">{fmt(t?.ltp)}</span>;
}

function OrderRow({ o, onView, open, result }) {
  const openOrder = useMarket((s) => s.openOrder);
  const cancel = async () => {
    try { await api.delete(`/orders/${o.id}`); toast.message(`Simulated order cancelled · ${o.symbol}`); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <tr className="border-b border-border/60 hover:bg-accent/40 cursor-pointer" onClick={() => onView(o)} data-testid={`order-row-${o.id}`}>
      <td className={`${td} num text-muted-foreground`}>{istTime(o.created_at)}</td>
      <td className={td}><span className={`text-[10px] font-bold rounded px-1.5 py-0.5 ${o.side === "BUY" ? "bg-up/15 text-up" : "bg-down/15 text-down"}`}>{o.side}</span></td>
      <td className={`${td} font-medium`}>{o.symbol} <span className="text-[10px] text-muted-foreground">{o.exchange}</span>
        {o.variety !== "REGULAR" && <span className="ml-1 text-[9px] text-amber-500 font-semibold">{o.variety}</span>}</td>
      <td className={`${td} text-muted-foreground`}>{o.product}</td>
      <td className={td}>{o.triggered_from || o.order_type}</td>
      <td className={`${td} num text-right`}>{o.filled_qty}/{o.quantity}</td>
      <td className={`${td} num text-right`}>{o.order_type === "MARKET" ? "MKT" : fmt(o.price)}{o.trigger_price ? <span className="text-muted-foreground"> / {fmt(o.trigger_price)} trg</span> : ""}</td>
      <td className={`${td} text-right`}>{open ? <Ltp k={o.instrument_key} /> : <span className="num">{o.avg_price ? fmt(o.avg_price) : "—"}</span>}</td>
      <td className={td}><Status o={o} /></td>
      <td className={`${td} text-right`} onClick={(e) => e.stopPropagation()}>
        {open && (<span className="flex justify-end gap-1">
          <button className={btnGhost} data-testid={`order-modify-${o.id}`} onClick={() => openOrder({ key: o.instrument_key, modify: o })}>Modify</button>
          <button className={`${btnGhost} text-down`} data-testid={`order-cancel-${o.id}`} onClick={cancel}>Cancel</button>
        </span>)}
        {!open && result?.pnl > 0 && (<span className="inline-flex items-center gap-2">
          <span className="num text-up text-[11.5px]" data-testid={`order-result-${o.id}`}>{signedPct(result.pct)}</span>
          <ShareRowButton testid={`order-share-${o.id}`} onClick={() => openShareFor({ orderId: o.id }).catch((e) => toast.error(errMsg(e)))} />
        </span>)}
        {!open && o.reason && <span className="text-[11px] text-down max-w-[240px] truncate inline-block align-middle" title={o.reason}>{o.reason}</span>}
      </td>
    </tr>
  );
}

function OrderTable({ rows, open, onView, testid, results }) {
  if (!rows.length) return <Empty testid={`${testid}-empty`}>{open ? "No open orders." : "No orders yet today."}</Empty>;
  return (
    <div className="overflow-x-auto thin-scroll">
      <table className="w-full" data-testid={testid}>
        <thead className="border-b border-border"><tr>
          {["Time", "Type", "Instrument", "Product", "Order", "Qty", "Price", open ? "LTP" : "Avg", "Status", ""].map((h, i) => <th key={i} className={`${th} ${[5, 6, 7].includes(i) ? "text-right" : ""}`}>{h}</th>)}
        </tr></thead>
        <tbody>{rows.map((o) => <OrderRow key={o.id} o={o} open={open} onView={onView} result={results?.[o.id]} />)}</tbody>
      </table>
    </div>
  );
}

function OrderSheet({ o, onClose }) {
  const trades = usePortfolio(o ? `/trades?order_id=${o.id}` : "/trades?order_id=none");
  return (
    <Sheet open={!!o} onOpenChange={(x) => !x && onClose()}>
      <SheetContent className="bg-card w-full sm:max-w-md" data-testid="order-detail-sheet">
        {o && (<>
          <SheetHeader><SheetTitle className="font-display">{o.side} {o.symbol}</SheetTitle></SheetHeader>
          <div className="mt-4 space-y-1 text-[12px]">
            {[["Order ID", o.id.slice(0, 12)], ["Status", o.status], ["Product", o.product], ["Variety", o.variety], ["Validity", o.validity],
              ["Qty", `${o.filled_qty}/${o.quantity}`], ["Avg fill", fmt(o.avg_price)], ["Blocked margin", fmt(o.blocked_margin)]].map(([k, v]) => (
              <div key={k} className="flex justify-between"><span className="text-muted-foreground">{k}</span><span className="num">{v}</span></div>))}
            {o.reason && <div className="text-down pt-2">{o.reason}</div>}
          </div>
          <div className="eyebrow mt-6 mb-2">Timeline</div>
          <ol className="border-l border-border ml-1 space-y-3">
            {o.history.map((h, i) => (
              <li key={i} className="pl-3 relative"><span className="absolute -left-[4px] top-1.5 h-2 w-2 rounded-full bg-primary" />
                <div className="text-[12px] font-medium">{h.status.replace("_", " ")}</div>
                <div className="text-[11px] text-muted-foreground">{h.note} <span className="num">· {istTime(h.at)}</span></div></li>))}
          </ol>
          <div className="eyebrow mt-6 mb-2">Fills</div>
          {(trades || []).map((t) => (
            <div key={t.id} className="flex justify-between text-[12px] num py-1 border-b border-border/60"><span>{t.qty} @ {fmt(t.price)}</span><span className="text-muted-foreground">charges {fmt(t.charges.total)}</span></div>))}
          {trades && !trades.length && <div className="text-[12px] text-muted-foreground">No fills.</div>}
        </>)}
      </SheetContent>
    </Sheet>
  );
}

export default function Orders() {
  const orders = usePortfolio("/orders") || [];
  const trades = usePortfolio("/trades") || [];
  const results = usePortfolio("/community/results");
  const [view, setView] = useState(null);
  const open = orders.filter((o) => OPEN.includes(o.status));
  const done = orders.filter((o) => !OPEN.includes(o.status));
  useSubscribe(open.map((o) => o.instrument_key));
  const current = view ? orders.find((o) => o.id === view.id) || view : null;
  return (
    <div className="p-4 lg:p-6" data-testid="orders-page">
      <PageHeader title="Orders" />
      <Tabs defaultValue="open">
        <TabsList className="mb-3">
          <TabsTrigger value="open" data-testid="orders-tab-open">Open ({open.length})</TabsTrigger>
          <TabsTrigger value="executed" data-testid="orders-tab-executed">Executed ({done.length})</TabsTrigger>
          <TabsTrigger value="trades" data-testid="orders-tab-trades">Trades ({trades.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="open"><Panel><OrderTable rows={open} open onView={setView} testid="open-orders-table" /></Panel></TabsContent>
        <TabsContent value="executed"><Panel><OrderTable rows={done} onView={setView} testid="executed-orders-table" results={results} /></Panel></TabsContent>
        <TabsContent value="trades">
          <Panel>
            {!trades.length ? <Empty testid="trades-empty">No trades today.</Empty> : (
              <div className="overflow-x-auto"><table className="w-full" data-testid="trades-table">
                <thead className="border-b border-border"><tr>{["Time", "Type", "Instrument", "Product", "Qty", "Price", "Charges"].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
                <tbody>{trades.map((t) => (
                  <tr key={t.id} className="border-b border-border/60">
                    <td className={`${td} num text-muted-foreground`}>{istTime(t.at)}</td>
                    <td className={`${td} ${t.side === "BUY" ? "text-up" : "text-down"} font-semibold text-[11px]`}>{t.side}</td>
                    <td className={`${td} font-medium`}>{t.symbol}</td><td className={td}>{t.product}</td>
                    <td className={`${td} num`}>{t.qty}</td><td className={`${td} num`}>{fmt(t.price)}</td><td className={`${td} num`}>{fmt(t.charges.total)}</td>
                  </tr>))}</tbody>
              </table></div>)}
          </Panel>
        </TabsContent>
      </Tabs>
      <OrderSheet o={current} onClose={() => setView(null)} />
    </div>
  );
}
